import {
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  NEVER,
  Observable,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  startWith,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LoaderColor, LoaderTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { NativeBridgeService } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils';
import { CmsDataService } from '@fcom/core/services';
import { ElementActions, ElementTypes, GaContext, MediaQueryService } from '@fcom/common';
import { finShare } from '@fcom/rx';
import { GtmService } from '@fcom/common/gtm';

import { ChatStatus, HelpButtonStatus } from '../../interfaces';
import { AgentforceChatService } from '../../services/agentforce-chat.service';

// The class of the div for "All agents are busy or offline, please try again later" message
// that we replace with our custom HTML
const OFFLINE_DIALOG_CLASS_NAME = 'embeddedServiceLiveAgentStateOfflineSupportDefaultUI';
const MODAL_OPEN_CLASS_NAME = 'modal-open';

@Component({
  selector: 'fin-chat',
  templateUrl: 'chat.component.html',
  styleUrls: ['chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatComponent implements OnInit, OnDestroy {
  private cmsDataService = inject(CmsDataService);
  private languageService = inject(LanguageService);
  private mediaQueryService = inject(MediaQueryService);
  private route = inject(ActivatedRoute);
  private readonly renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  private gtmService = inject(GtmService);
  private afChatService = inject(AgentforceChatService);
  private nativeBridgeService = inject(NativeBridgeService);
  private router = inject(Router);

  private subscription = new Subscription();
  private allAgentsAreBusyOrOfflineSubscription = new Subscription();

  readonly HelpButtonStatus = HelpButtonStatus;
  readonly LoaderTheme = LoaderTheme;
  readonly LoaderColor = LoaderColor;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  chatStatus: ChatStatus = ChatStatus.INITIAL;
  chatButtonState$: BehaviorSubject<HelpButtonStatus>;
  disableChat$: BehaviorSubject<boolean>;
  openChatTrigger$: BehaviorSubject<HelpButtonStatus> = new BehaviorSubject(HelpButtonStatus.VISIBLE);
  dirty$: Observable<boolean>;
  renderAudioElement = false;
  isNewBrand = computed(() => this.afChatService.isNewBrand());

  @ViewChild('target', { static: true }) target: ElementRef;

  // This is to set the mask all chat content in Hotjar sessions
  @HostBinding('attr.data-hj-suppress') maskHotJar = '';
  // ...and hide it in Datadog Session Replay
  @HostBinding('attr.data-dd-privacy') maskDatadog = 'hidden';

  ngOnInit(): void {
    // Disable chat in app
    if (this.nativeBridgeService.isInsideNativeWebView) {
      return;
    }
    this.chatButtonState$ = this.afChatService.chatButtonState$;
    this.disableChat$ = this.afChatService.disableChat$;

    this.subscription.add(
      combineLatest([
        this.router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          startWith({ url: this.router.url } as NavigationEnd),
          map((event: NavigationEnd): string => event.url)
        ),
        this.languageService.locale,
        this.languageService.lang,
      ]).subscribe(([url, locale, langValue]): void => {
        const isIncidentUpdates: boolean = url.includes('/incident-updates');
        const isRussianLocale: boolean = locale.includes('RU');

        this.afChatService.setAgentforceLanguage(langValue);

        // Disable chat when either locale is RU or page is incident-updates (darksite)
        if (isIncidentUpdates || isRussianLocale) {
          this.afChatService.disableChat$.next(true);
          this.afChatService.chatButtonState$.next(HelpButtonStatus.HIDDEN);
          return;
        }

        // Re-enable chat if it was previously disabled and conditions are now met (Not RU or darksite)
        if (this.disableChat$.value) {
          this.afChatService.chatButtonState$.next(HelpButtonStatus.VISIBLE);
          this.afChatService.disableChat$.next(false);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.afChatService.openWebMessagingChat$, this.afChatService.messagingForWebTriggered$]).subscribe(
        ([fields, hasBeenTriggered]): void => {
          if (hasBeenTriggered) {
            this.afChatService.startWebMessagingChat(fields);
            return;
          }

          this.afChatService.webMessagingChat(fields, this.target);
        }
      )
    );

    this.subscription.add(
      this.afChatService.renderAudioElement$.pipe(filter((allowAudio): boolean => !!allowAudio)).subscribe((): void => {
        this.renderAudioElement = true;
      })
    );

    // Mimic the same behavior from the old SF chat button when chat button is
    // clicked once then it is visible always and everywhere until refresh of the page
    this.dirty$ = this.afChatService.chatButtonState$.pipe(
      filter((helpButtonStatus): boolean => helpButtonStatus !== HelpButtonStatus.VISIBLE),
      map((): boolean => true),
      take(1),
      finShare()
    );

    this.subscription.add(
      // prevent from scrolling page when chat and prechat screens are open in mobile
      combineLatest([this.mediaQueryService.isBreakpoint$('mobile'), this.afChatService.chatButtonState$])
        .pipe(
          map(([isMobile, openChatTrigger]): boolean => isMobile && openChatTrigger !== HelpButtonStatus.VISIBLE),
          distinctUntilChanged()
        )
        .subscribe((shouldSetModalOpen): void => {
          if (shouldSetModalOpen) {
            this.renderer.addClass(this.document.body, MODAL_OPEN_CLASS_NAME);
          } else {
            this.renderer.removeClass(this.document.body, MODAL_OPEN_CLASS_NAME);
          }
        })
    );

    this.subscription.add(
      this.route.queryParams
        .pipe(
          take(1),
          filter((params): boolean => params.openChat?.toLowerCase() === 'true')
        )
        .subscribe((): void => {
          this.chatTrigger(HelpButtonStatus.CLICKED);
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
    unsubscribe(this.allAgentsAreBusyOrOfflineSubscription);
  }

  /*
   * Listens to events of MutationObserver (finMutationObserver) and replaces the basic unlocalized
   * "All agents are offline or busy" message with a Finnair custom message that contains chat's service
   * hours and information that the US site's chat is open 24/7
   */
  onDomChange($event: MutationRecord): void {
    const addedNodes: NodeList = $event.addedNodes;
    addedNodes.forEach((node: Element) => {
      if (node.classList?.contains(OFFLINE_DIALOG_CLASS_NAME)) {
        // The "All agents are busy or offline" message appeared, replace it
        // with a HTML fragment from CMS
        this.subscription.remove(this.allAgentsAreBusyOrOfflineSubscription);
        this.allAgentsAreBusyOrOfflineSubscription = this.languageService
          .translate('fragments.chatAgentsBusyOrOffline.url')
          .pipe(
            filter((v) => !!v),
            switchMap((url) => (!url ? NEVER : this.cmsDataService.getFragmentJson(url))),
            filter((s: { detailText?: string }) => !!s.detailText),
            map((s: { detailText?: string }) => s.detailText || ''),
            take(1)
          )
          .subscribe((detailTextHtml: string) => {
            node.innerHTML = detailTextHtml;
          });
        this.subscription.add(this.allAgentsAreBusyOrOfflineSubscription);
      }
    });
  }

  chatTrigger(shouldOpen: HelpButtonStatus): void {
    this.afChatService.chatButtonState$.next(shouldOpen);

    if (shouldOpen === HelpButtonStatus.CLICKED) {
      this.trackElement('open-chat-button');
    }
  }

  private trackElement(label: string): void {
    this.gtmService.trackElement(`${label}`, GaContext.CHAT, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }
}
